import React from 'react';
import { Link } from 'react-router-dom';
import { Image as ImageIcon, ExternalLink, Target, Megaphone, Layers, ClipboardList } from 'lucide-react';
import { Card, SectionHeader } from '@/components/Card';
import { Lead } from '@/services/leads.service';
import { useModuleAccess } from '@/contexts/ModuleAccessContext';
import clsx from 'clsx';

interface Props {
  lead: Lead;
}

const ProvenanceChip: React.FC<{
  icon: React.ReactNode;
  label: string;
  to?: string;
}> = ({ icon, label, to }) => {
  const inner = (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-alt px-2.5 py-1 text-xs font-medium text-text-secondary',
        to && 'hover:border-primary hover:text-primary transition-colors',
      )}
    >
      {icon}
      <span className="truncate max-w-[160px]">{label}</span>
      {to && <ExternalLink size={10} className="shrink-0 opacity-60" />}
    </span>
  );
  if (to) return <Link to={to}>{inner}</Link>;
  return inner;
};

export const LeadProvenanceCard: React.FC<Props> = ({ lead }) => {
  const { has } = useModuleAccess();
  const metaSource = lead.source === 'ctwa' || lead.source === 'lead_ad_form';
  const googleSource = lead.source === 'google_lead_form' || lead.source === 'google_ads';
  const groupLabel = metaSource ? 'Ad Set' : 'Ad group';
  const adLabel = googleSource ? 'Creative' : 'Ad';
  const sourceLabel = lead.source.replace(/_/g, ' ');
  const sourceUrl = lead.referralSourceUrl && /^https?:\/\//i.test(lead.referralSourceUrl) ? lead.referralSourceUrl : undefined;
  // Prefer the ImageKit mirror — FB's signed referral image URL expires
  // in a few hours, leaving the provenance card with a broken image.
  const referralImg = lead.referralImagekitUrl || lead.referralImageUrl;
  const hasProvenance =
    lead.referralHeadline ||
    referralImg ||
    lead.sourceCampaignId ||
    lead.sourceAdSetId ||
    lead.sourceAdId ||
    lead.sourceFormId;

  if (!hasProvenance) {
    return (
      <Card padded>
        <SectionHeader title="Lead source" icon={<Target size={15} />} />
        <p className="text-sm capitalize text-text-secondary">{sourceLabel}</p>
        <p className="mt-1 text-xs text-text-muted">No campaign or ad identifiers were provided.</p>
      </Card>
    );
  }

  return (
    <Card padded>
      <SectionHeader title="Lead source" icon={<Target size={15} />} />

      <p className="mb-3 text-xs capitalize text-text-muted">{sourceLabel}</p>
      {/* Ad creative preview */}
      {referralImg && (
        <div className="mb-3 overflow-hidden rounded-xl border border-border">
          <img
            src={referralImg}
            alt={lead.referralHeadline ?? 'Ad creative'}
            className="block w-full max-h-40 object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).parentElement!.style.display = 'none';
            }}
          />
        </div>
      )}
      {!referralImg && (lead.referralHeadline || lead.referralBody) && (
        <div className="mb-3 flex h-20 items-center justify-center rounded-xl border border-border bg-surface-alt text-text-muted">
          <ImageIcon size={22} />
        </div>
      )}

      {/* Headline & body */}
      {lead.referralHeadline && (
        <p className="mb-0.5 text-sm font-semibold text-text-primary leading-snug">
          {lead.referralHeadline}
        </p>
      )}
      {lead.referralBody && (
        <p className="mb-3 text-xs text-text-secondary leading-relaxed">{lead.referralBody}</p>
      )}

      {/* Provenance chain chips */}
      <div className="flex flex-col gap-1.5">
        {lead.sourceFormId && (
          <ProvenanceChip
            icon={<ClipboardList size={11} />}
            label={`Lead form: ${lead.sourceFormId}`}
          />
        )}
        {lead.sourceCampaignId && (
          <ProvenanceChip
            icon={<Megaphone size={11} />}
            label={`Campaign ${lead.sourceCampaignId}`}
            to={metaSource && has('facebook_ads') ? `/facebook/campaigns/${lead.sourceCampaignId}` : undefined}
          />
        )}
        {lead.sourceAdSetId && (
          <ProvenanceChip
            icon={<Layers size={11} />}
            label={`${groupLabel} ${lead.sourceAdSetId}`}
            to={metaSource && has('facebook_ads') ? `/facebook/ad-sets/${lead.sourceAdSetId}` : undefined}
          />
        )}
        {lead.sourceAdId && (
          <ProvenanceChip
            icon={<Target size={11} />}
            label={`${adLabel} ${lead.sourceAdId}`}
          />
        )}
      </div>

      {/* Source URL */}
      {sourceUrl && (
        <a
          href={sourceUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-[11px] text-text-muted hover:text-primary transition-colors"
        >
          View original ad <ExternalLink size={9} />
        </a>
      )}
    </Card>
  );
};
